import { useState, useEffect, useRef, useMemo } from 'react';
import {
  COUNTRY_FILTER_ALL,
  COUNTRY_FILTER_FEATURED,
} from '../../config/constants';
import {
  COUNTRIES,
  getDisplayCountry,
  getCountryCurrencyLabel,
  getRegionalMarketLabel,
} from '../../data/countries';
import CountryFlag from './CountryFlag';

export const getMarketButtonLabel = (selected) => {
  if (selected === COUNTRY_FILTER_ALL) return "All countries";
  if (selected === COUNTRY_FILTER_FEATURED) return "Featured markets";
  return getDisplayCountry(selected).name;
};

const CountrySelector = ({ selected, onChange, centered = false, variant = "default" }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const rootRef = useRef(null);
  const c = getDisplayCountry(selected);
  const isHeroBar = variant === "heroBar";
  const isBrowsePanel = variant === "browsePanel";
  const isSearchCombo = variant === "searchCombo";

  useEffect(() => {
    if (!open) return;
    const closeOnOutside = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOpen(false);
        setQuery("");
      }
    };
    const timer = setTimeout(() => document.addEventListener("click", closeOnOutside), 0);
    return () => {
      clearTimeout(timer);
      document.removeEventListener("click", closeOnOutside);
    };
  }, [open]);

  const pinnedOptions = useMemo(() => ([
    [COUNTRY_FILTER_FEATURED, getDisplayCountry(COUNTRY_FILTER_FEATURED)],
    [COUNTRY_FILTER_ALL, getDisplayCountry(COUNTRY_FILTER_ALL)],
  ]), []);

  const countryList = useMemo(() => {
    const entries = Object.entries(COUNTRIES).filter(([code]) => code !== "GB");
    return entries.sort((a, b) => a[1].name.localeCompare(b[1].name));
  }, []);

  const filteredCountries = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return countryList;
    return countryList.filter(([code, country]) =>
      country.name.toLowerCase().includes(q) ||
      code.toLowerCase().includes(q) ||
      (country.currency || "").toLowerCase().includes(q)
    );
  }, [countryList, query]);

  const rootClass = isSearchCombo
    ? "country-selector-root country-selector-root--search-combo"
    : isBrowsePanel
      ? "country-selector-root country-selector-root--browse-panel"
      : isHeroBar
        ? "country-selector-root country-selector-root--hero-bar"
        : centered
          ? "country-selector-root country-selector-root--centered"
          : "country-selector-root";

  const triggerClass = isSearchCombo
    ? "country-selector country-selector--search-combo"
    : isBrowsePanel
      ? "country-selector country-selector--browse-panel"
      : isHeroBar
        ? "country-selector country-selector--hero-bar"
        : `country-selector${centered ? " country-selector--listings" : ""}`;

  const menuClass = isSearchCombo
    ? "country-selector-menu country-selector-menu--search-combo"
    : isBrowsePanel
      ? "country-selector-menu country-selector-menu--browse-panel"
      : isHeroBar
        ? "country-selector-menu country-selector-menu--hero-bar"
        : centered
          ? "country-selector-menu country-selector-menu--centered"
          : "country-selector-menu";

  const buttonLabel = (isBrowsePanel || isSearchCombo)
    ? getRegionalMarketLabel(selected)
    : getMarketButtonLabel(selected);

  const optionClass = (code, pinned = false) =>
    `country-selector-option${code === selected ? " is-selected" : ""}${pinned ? " is-pinned" : ""}`;

  const menuStyle = {
    position: "absolute",
    top: "100%",
    right: isSearchCombo || isBrowsePanel || isHeroBar ? 0 : centered ? undefined : 0,
    left: isSearchCombo || isBrowsePanel ? 0 : centered && !isHeroBar ? "50%" : undefined,
    transform: isSearchCombo || isBrowsePanel ? undefined : centered && !isHeroBar ? "translateX(-50%)" : undefined,
    minWidth: isSearchCombo
      ? "min(300px, 85vw)"
      : isBrowsePanel
        ? "100%"
        : isHeroBar
          ? "min(320px, 88vw)"
          : centered
            ? "min(360px, 92vw)"
            : "280px",
    border: "1px solid var(--border)",
    borderRadius: "8px",
    marginTop: "0.35rem",
    boxShadow: "0 12px 40px rgba(0,0,0,0.18)",
  };

  return (
    <div ref={rootRef} className={rootClass} style={{ position: "relative" }}>
      <div
        className={triggerClass}
        onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); if (!open) setQuery(""); }}
        title={c.name}
        role="button"
        tabIndex={0}
        aria-haspopup="listbox"
        aria-expanded={open}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setOpen((v) => !v);
            if (!open) setQuery("");
          }
        }}
      >
        <CountryFlag
          code={selected}
          imgWidth={isBrowsePanel || isSearchCombo ? 22 : 20}
          emojiSize={isBrowsePanel || isSearchCombo ? "1.1rem" : isHeroBar ? "1rem" : "1.1rem"}
          className={isBrowsePanel || isSearchCombo ? "browse-panel-flag" : ""}
        />
        <span className="country-label-long">{buttonLabel}</span>
        <i
          className={`fas fa-chevron-${open ? "up" : "down"}`}
          style={{
            fontSize: "0.65rem",
            color: isBrowsePanel ? undefined : isHeroBar ? undefined : "var(--text-light)",
            marginLeft: "auto",
          }}
          aria-hidden="true"
        />
      </div>
      {open && (
        <div
          className={menuClass}
          style={menuStyle}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="country-selector-search">
            <input
              type="text"
              placeholder="Search countries..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              autoFocus
              aria-label="Filter countries"
            />
          </div>
          <div className="country-selector-list">
            {!query.trim() && pinnedOptions.map(([code, country]) => (
              <div
                key={code}
                className={optionClass(code, true)}
                role="option"
                aria-selected={code === selected}
                onClick={() => { onChange(code); setOpen(false); setQuery(""); }}
              >
                <CountryFlag code={code} imgWidth={22} emojiSize="1.2rem" />
                <span>{country.name}</span>
              </div>
            ))}
            {filteredCountries.length === 0 ? (
              <div className="country-selector-empty">No countries found</div>
            ) : filteredCountries.map(([code, country]) => (
              <div
                key={code}
                className={optionClass(code)}
                role="option"
                aria-selected={code === selected}
                onClick={() => { onChange(code); setOpen(false); setQuery(""); }}
              >
                <CountryFlag code={code} imgWidth={22} emojiSize="1.2rem" />
                <span>{country.name}</span>
                <span
                  className="country-option-currency"
                  style={{ marginLeft: "auto", fontSize: "0.8rem", minWidth: "2.5rem", textAlign: "right" }}
                  title={getCountryCurrencyLabel(country) ? "" : "Display uses USD until local FX is available"}
                >
                  {getCountryCurrencyLabel(country) || "—"}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default CountrySelector;
